import React, { useState, useEffect } from 'react';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardCheck } from 'lucide-react';
import CollapsibleSection from '../ui/CollapsibleSection';
import { formatDate } from '../utils/dateFormatter';
import useApi from '../api';
import { toast } from 'react-toastify';
import SectionHeader from '@/components/ui/SectionHeader';

interface Assessment {
    id: string;
    related_assessment_id: string;
    assessment_status: string;
    assessment_responses: { [key: string]: string };
    created_at: string;
    updated_at: string;
    conversation_id: string;
    visitor_id?: string;
    lead_id?: string;
    assessment_name?: string;
}

interface AssessmentsProps {
    // Either pass assessments directly OR a leadId to fetch them
    assessments?: Assessment[];
    leadId?: string;
    onAssessmentDeleted?: () => void;
    compact?: boolean; // When true, use smaller headers for session details view
    variant?: 'card' | 'panel'; // 'panel' renders as a borderless collapsible section
    sectionStateKey?: string; // localStorage prefix for collapse persistence (panel variant)
    contactDetail?: boolean; // match the full contact page's section-heading treatment
}

const Assessments: React.FC<AssessmentsProps> = ({ assessments: propAssessments, leadId, onAssessmentDeleted, compact = false, variant = 'card', sectionStateKey, contactDetail = false }) => {
    const [assessments, setAssessments] = useState<Assessment[]>(propAssessments || []);
    const [loading, setLoading] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
    const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
    const [deleteAssessmentLoading, setDeleteAssessmentLoading] = useState(false);
    const [deleteAssessmentDialogOpen, setDeleteAssessmentDialogOpen] = useState(false);
    const { remove, get } = useApi();

    // Fetch assessments if leadId is provided
    useEffect(() => {
        if (leadId) {
            fetchAssessments();
        }
    }, [leadId]);

    // Update assessments when prop changes
    useEffect(() => {
        if (propAssessments) {
            setAssessments(propAssessments);
        }
    }, [propAssessments]);

    const fetchAssessments = async () => {
        if (!leadId) return;
        
        setLoading(true);
        try {
            const response = await get(`/leads/${leadId}/assessments`);
            setAssessments(response.data.data || []);
        } catch (error) {
            console.error('Error fetching assessments:', error);
            toast.error('Failed to load assessments');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAssessment = async () => {
        if (!selectedAssessment) return;

        setDeleteAssessmentLoading(true);
        try {
            await remove(`/assessments/${selectedAssessment.id}`);
            toast.success('Assessment deleted successfully');
            setAssessmentDialogOpen(false);
            setDeleteAssessmentDialogOpen(false);
            
            // Refresh assessments
            if (leadId) {
                fetchAssessments();
            } else if (onAssessmentDeleted) {
                onAssessmentDeleted();
            }
        } catch (error) {
            console.error('Error deleting assessment:', error);
            toast.error('Failed to delete assessment. Please try again.');
        } finally {
            setDeleteAssessmentLoading(false);
        }
    };

    if (loading) {
        if (contactDetail) {
            return (
                <div className="space-y-2">
                    <SectionHeader title="Assessments" icon={ClipboardCheck} />
                    <div className="rounded-xl border bg-card overflow-hidden animate-pulse">
                        <div className="divide-y">
                            {Array.from({ length: 2 }).map((_, i) => (
                                <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-4">
                                    <div className="h-4 w-36 bg-muted rounded" />
                                    <div className="h-5 w-16 bg-muted rounded-full" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="rounded-xl border bg-card overflow-hidden animate-pulse">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div className="h-4 w-24 bg-muted rounded" />
                    <div className="h-4 w-4 bg-muted rounded" />
                </div>
                <div className="divide-y">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-4">
                            <div className="space-y-1.5 flex-1">
                                <div className="h-4 w-36 bg-muted rounded" />
                                <div className="h-3 w-24 bg-muted rounded" />
                            </div>
                            <div className="h-5 w-16 bg-muted rounded-full" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (assessments.length === 0) {
        return null;
    }

    const isPanel = variant === 'panel';
    const rowPadX = isPanel ? 'px-0' : 'px-4';

    const assessmentRows = (
        <div className="divide-y">
            {assessments.map((assessment) => (
                <div
                    key={assessment.id}
                    className={`${rowPadX} flex cursor-pointer items-center justify-between gap-4 py-2.5 transition-colors hover:bg-muted/40 ${isPanel ? 'rounded-md' : ''}`}
                    onClick={() => {
                        setSelectedAssessment(assessment);
                        setAssessmentDialogOpen(true);
                    }}
                >
                    <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium truncate block">
                            {assessment.assessment_name || assessment.related_assessment_id}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {Object.keys(assessment.assessment_responses || {}).length} responses · {formatDate(assessment.created_at, 'short')}
                        </span>
                    </div>
                    <Badge
                        variant={
                            assessment.assessment_status === 'completed' ? 'default' :
                                assessment.assessment_status === 'active' ? 'secondary' :
                                    'outline'
                        }
                        className={`shrink-0 ${isPanel ? 'text-[10px]' : ''}`}
                    >
                        {assessment.assessment_status}
                    </Badge>
                </div>
            ))}
        </div>
    );

    return (
        <>
            {contactDetail ? (
                <div className="space-y-2">
                    <SectionHeader title="Assessments" icon={ClipboardCheck} />
                    <div className="rounded-xl border bg-card overflow-hidden">
                        {assessmentRows}
                    </div>
                </div>
            ) : isPanel ? (
                <CollapsibleSection
                    title="Assessments"
                    icon={<ClipboardCheck className="w-4 h-4 text-muted-foreground" />}
                    storageKey={sectionStateKey ? `${sectionStateKey}:Assessments` : undefined}
                >
                    {assessmentRows}
                </CollapsibleSection>
            ) : (
                <div className="rounded-xl border bg-card overflow-hidden">
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Assessments</h3>
                        <ClipboardCheck className="w-4 h-4 text-muted-foreground" />
                    </div>
                    {assessmentRows}
                </div>
            )}

            <Dialog open={assessmentDialogOpen} onOpenChange={setAssessmentDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto overflow-x-hidden [&_td]:break-words">
                    <DialogHeader>
                        <DialogTitle>Assessment Details</DialogTitle>
                        <DialogDescription>
                            {selectedAssessment ? (
                                selectedAssessment.assessment_name || `Assessment ID: ${selectedAssessment.related_assessment_id}`
                            ) : ''}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedAssessment && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Assessment Information</h3>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell className="font-medium">Assessment Name</TableCell>
                                            <TableCell>{selectedAssessment.assessment_name || selectedAssessment.related_assessment_id}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className="font-medium">Status</TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        selectedAssessment.assessment_status === 'completed' ? 'default' :
                                                            selectedAssessment.assessment_status === 'active' ? 'secondary' :
                                                                'outline'
                                                    }
                                                >
                                                    {selectedAssessment.assessment_status}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className="font-medium">Created</TableCell>
                                            <TableCell>{formatDate(selectedAssessment.created_at)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className="font-medium">Last Updated</TableCell>
                                            <TableCell>{formatDate(selectedAssessment.updated_at)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className="font-medium">Conversation ID</TableCell>
                                            <TableCell>{selectedAssessment.conversation_id}</TableCell>
                                        </TableRow>
                                        {selectedAssessment.visitor_id && (
                                            <TableRow>
                                                <TableCell className="font-medium">Visitor ID</TableCell>
                                                <TableCell>{selectedAssessment.visitor_id}</TableCell>
                                            </TableRow>
                                        )}
                                        {selectedAssessment.lead_id && (
                                            <TableRow>
                                                <TableCell className="font-medium">Lead ID</TableCell>
                                                <TableCell>{selectedAssessment.lead_id}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>

                            {selectedAssessment.assessment_responses && Object.keys(selectedAssessment.assessment_responses).length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Assessment Responses</h3>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Question</TableHead>
                                                <TableHead>Response</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {Object.entries(selectedAssessment.assessment_responses).map(([question, response]) => (
                                                <TableRow key={question}>
                                                    <TableCell className="font-medium break-words max-w-xs">
                                                        {question}
                                                    </TableCell>
                                                    <TableCell className="break-words max-w-xs">
                                                        {response}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {(!selectedAssessment.assessment_responses || Object.keys(selectedAssessment.assessment_responses).length === 0) && (
                                <div className="text-center py-8 text-gray-500">
                                    No responses recorded yet
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter className="flex justify-between">
                        <Button
                            variant="destructive"
                            onClick={() => setDeleteAssessmentDialogOpen(true)}
                            disabled={deleteAssessmentLoading}
                        >
                            Delete Assessment
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setAssessmentDialogOpen(false)}
                            disabled={deleteAssessmentLoading}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteAssessmentDialogOpen} onOpenChange={setDeleteAssessmentDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Assessment</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this assessment? This action cannot be undone.
                            All assessment responses and data will be permanently removed.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteAssessmentDialogOpen(false)}
                            disabled={deleteAssessmentLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteAssessment}
                            disabled={deleteAssessmentLoading}
                        >
                            {deleteAssessmentLoading ? 'Deleting...' : 'Delete Assessment'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Assessments;
